import {clamp} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {
  NonTableWYCIWYPPart,
  TableWYCIWYPPart,
  WYCIWYPPart,
} from 'notebook/mediocre/clipboard/WyciwypConstants';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {MAX_INDENTATION} from 'notebook/notes/lists/ListConstants';
import NoteConstants from 'notebook/notes/NoteConstants';
import excess from 'notebook/tables/excess';
import sanitizeCell from 'notebook/tables/paste/sanitizeCell';
import {NotePart, ServerNotePart} from 'notebook/util/NoteUtilConstants';

const {TABLE, TEXT, CODE} = NoteConstants;

type DefaultNotePart<ClientId extends boolean> = ClientId extends true ? NotePart : ServerNotePart;

export function makeDefaultItem<T extends boolean = true>(
  type: string | null = null,
  text?: string | null,
  {
    shouldGenerateCid = true as T,
    initialNotePart = false,
  }: {shouldGenerateCid?: T; initialNotePart?: boolean} = {}
): DefaultNotePart<T> {
  if (type == null) {
    type = NoteConstants.TEXT;
  }
  if (text == null) {
    text = '';
  }
  const baseItem = {
    type,
    text,
    indentation: 0,
    listType: null,
    alignment: null,
    important: false,
    itemWidth: null,
    table: null,
    item_id: null,
    item: null,
    itemLink: null,
    headerType: null,
  };

  if (initialNotePart) {
    return {
      ...baseItem,
      code: type === NoteConstants.CODE ? '' : null,
      placeholder: false,
    } as DefaultNotePart<T>;
  }
  return {
    ...baseItem,
    cid: shouldGenerateCid ? _.uniqueId('note-part') : null,
    code: '',
    headerType: NoteConstants.PARAGRAPH,
    formats: formatters.defaultFormats(),
  } as DefaultNotePart<T>;
}

export function makeItemFromWyciwypPart(
  part: WYCIWYPPart,
  usedTableNames: Array<string>,
  initialNotePart: boolean = false
): {item: NotePart; wasIndentationCapped: boolean} {
  const {formats, type, listType} = part;
  let {indentation, alignment} = part;

  // Check if indentation needs to be capped.
  indentation = indentation != null ? indentation : (indentation = 0);
  const wasIndentationCapped = indentation > MAX_INDENTATION;
  indentation = clamp(0, MAX_INDENTATION, indentation);

  const lastItem = (() => {
    switch (type) {
      case wyciwyp.TABLE: {
        const tablePart = part as unknown as TableWYCIWYPPart;
        const tableName = excess.util.uniqueTableName(usedTableNames);
        usedTableNames.push(tableName);
        const tableText = reactUpdate(tablePart.text, {
          $merge: {
            name: tableName,
            table: tablePart.text.table.map((row, i) =>
              row.map((cell, j) => sanitizeCell(cell, {row: i, column: j}))
            ),
          },
        });
        return reactUpdate(makeDefaultItem(TABLE, null, {initialNotePart}), {
          $merge: {table: tableText, indentation},
        });
      }
      case wyciwyp.CODE:
        return reactUpdate(makeDefaultItem(CODE, null, {initialNotePart}), {
          $merge: {code: (part as unknown as NonTableWYCIWYPPart).code, indentation, listType},
        });
      default:
        alignment = alignment != null ? alignment : null;
        return reactUpdate(makeDefaultItem(TEXT, null, {initialNotePart}), {
          $merge: {
            text: (part as unknown as NonTableWYCIWYPPart).text,
            formats,
            listType,
            indentation,
            alignment,
          },
        });
    }
  })();
  return {item: lastItem, wasIndentationCapped};
}

export function makeFilePlaceholderItem(taskId: string | null = null, type: string | null = null): NotePart {
  if (type == null) {
    type = NoteConstants.NOTE_LINKED_OBJECT;
  }
  // eslint-disable-next-line no-restricted-syntax
  const placeholderItem = _.extend(makeDefaultItem(type), {
    alignment: 'left',
    placeholder: true,
  });
  if (taskId != null) {
    placeholderItem.taskId = taskId;
  }
  return placeholderItem;
}
